import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import type { ContactsCountry } from "app/pages/sales-intelligence/modules/company-analysis/types/contacts";
import type { QuotaOperationState } from "app/pages/sales-intelligence/modules/quota/types";
import type {
    ContactDetails,
    ContactSearchEntry,
    RevealedContactSearchEntry,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";
import { DEFAULT_PAGE } from "app/pages/sales-intelligence/sub-modules/right-sidebar/constants/contacts";
import type { FetchError } from "app/pages/sales-intelligence/types";

import * as actions from "../actions/actions";

export type ContactsTabReducerType = {
    contacts: (ContactSearchEntry | RevealedContactSearchEntry)[];
    isContactsLoading: boolean;
    isCountriesLoading: boolean;
    contactsFetchError: string | null | FetchError;
    totalCount: number | null;
    page: number | null;
    searchTerm: string | null;
    countries: ContactsCountry[] | null;
    selectedCountriesIds: readonly number[] | null;
    countriesFetchError: string | null | FetchError;
    contactDetails: ContactDetails | null;
    currentProfile: ProfileDto | null;
    editedProfile: ProfileDto | null;
    isContactsUpdating: boolean;
    revealingError: string | null | FetchError;
    quota: QuotaOperationState | null;
    isQuotaFetching: boolean;
};

const initialState: ContactsTabReducerType = {
    isContactsLoading: false,
    contacts: [],
    totalCount: null,
    countries: [],
    selectedCountriesIds: [],
    isCountriesLoading: false,
    countriesFetchError: null,
    page: DEFAULT_PAGE,
    searchTerm: "",
    contactsFetchError: null,
    contactDetails: null,
    currentProfile: null,
    isContactsUpdating: false,
    revealingError: null,
    editedProfile: null,
    quota: null,
    isQuotaFetching: false,
};

const contactsTabReducer = createReducer<ContactsTabReducerType, ActionType<typeof actions>>(initialState)
    .handleAction(actions.fetchTabContacts.request, (state, { payload }) => ({
        ...state,
        isContactsLoading: !payload,
        isContactsUpdating: payload,
    }))
    .handleAction(actions.fetchTabContacts.failure, (state, { payload }) => ({
        ...state,
        contactsFetchError: payload,
        isContactsLoading: false,
        isContactsUpdating: false,
    }))
    .handleAction(actions.fetchTabContacts.success, (state, { payload }) => ({
        ...state,
        contacts: payload.contacts,
        totalCount: payload.totalCount,
        isContactsLoading: false,
        isContactsUpdating: false,
    }))
    .handleAction(actions.setPage, (state, { payload }) => ({
        ...state,
        page: payload,
    }))
    .handleAction(actions.setSearchTerm, (state, { payload }) => ({
        ...state,
        searchTerm: payload,
    }))
    .handleAction(actions.fetchContactsCountries.request, (state) => ({
        ...state,
        isCountriesLoading: true,
    }))
    .handleAction(actions.fetchContactsCountries.failure, (state, { payload }) => ({
        ...state,
        isCountriesLoading: false,
        countriesFetchError: payload,
    }))
    .handleAction(actions.fetchContactsCountries.success, (state, { payload }) => ({
        ...state,
        isCountriesLoading: false,
        countries: payload,
    }))
    .handleAction(actions.fetchContactsQuota.request, (state) => ({
        ...state,
        isQuotaFetching: true,
    }))
    .handleAction(actions.fetchContactsQuota.failure, (state) => ({
        ...state,
        isQuotaFetching: false,
    }))
    .handleAction(actions.fetchContactsQuota.success, (state, { payload }) => ({
        ...state,
        isQuotaFetching: false,
        quota: payload,
    }))
    .handleAction(actions.setCurrentCountriesIds, (state, { payload }) => ({
        ...state,
        selectedCountriesIds: payload,
    }))
    .handleAction(actions.setContactTabDetails, (state, { payload }) => ({
        ...state,
        contactDetails: payload,
    }))
    .handleAction(actions.setCurrentProfile, (state, { payload }) => ({
        ...state,
        currentProfile: payload,
    }))
    .handleAction(actions.setEditedProfile, (state, { payload }) => ({
        ...state,
        editedProfile: payload,
    }))
    .handleAction(actions.changeRemainingQuota, (state, { payload }) => ({
        ...state,
        quota: { ...payload },
    }));

export default contactsTabReducer;
